import {
  useGetPermissions,
  useGetRole,
  usePermissions,
  useUpsertRole,
} from '@dispatch/shared/hooks';
import { useOutletContext, useParams } from 'react-router-dom';
import { useNotificationMutationFactory } from '@dispatch/web/ui';
import { Role, RoleUpsertBody } from '@dispatch/shared/types';
import { RoleDetails } from '@dispatch/web/features/roles';
import { useCurrentUser } from '@dispatch/web/auth';

const RoleDetailsPage = () => {
  const outletContext: {
    refetch: () => Promise<void>;
  } = useOutletContext();
  const { tenantId } = useCurrentUser();
  const params = useParams();
  const id: string = params?.roleId || '';
  const { data: role, status } = useGetRole(id);
  const { data: permissions } = useGetPermissions();
  const { permissions: userPermissions } = usePermissions();
  const { notifySuccess } = useNotificationMutationFactory('Role', 'update');
  const onSuccess = () => {
    notifySuccess();
    outletContext?.refetch?.();
  };
  const { upsertRole, status: mutateStatus } = useUpsertRole(id, onSuccess);

  const handleSave = (values: Partial<Role>) => {
    const postBody: RoleUpsertBody = {
      ...values,
      id,
      tenantId: tenantId || '',
      application: 'Dispatch',
      updatedAt: new Date().toISOString(),
      createdAt: role?.data?.createdAt,
      permissions: values.permissions as string[],
      roleType: role?.data?.roleType,
    };

    upsertRole(postBody);
  };

  const handleDeactivate = (values: Role) => {
    if (!values) return;

    const postBody: RoleUpsertBody = {
      ...values,
      id,
      tenantId: tenantId || '',
      application: 'Dispatch',
      updatedAt: new Date().toISOString(),
      deactivatedAt: new Date().toISOString(),
      permissions: values.permissions as string[],
      roleType: role?.data?.roleType,
    };

    upsertRole(postBody);
  };

  const handleActivate = (values: Role) => {
    if (!values) return;

    const postBody: RoleUpsertBody = {
      ...values,
      id,
      tenantId: tenantId || '',
      application: 'Dispatch',
      updatedAt: new Date().toISOString(),
      deactivatedAt: null,
      permissions: values.permissions as string[],
      roleType: role?.data?.roleType,
    };

    upsertRole(postBody);
  };

  return (
    <RoleDetails
      onDeactivate={handleDeactivate}
      onActivate={handleActivate}
      onSave={handleSave}
      status={mutateStatus === 'loading' ? mutateStatus : status}
      data={role?.data}
      permissions={permissions?.data || []}
      userPermissions={userPermissions}
    />
  );
};

export default RoleDetailsPage;
